import { IconWarningRegular, skinVars, Stack, Text3, useDialog } from '@telefonica/mistica';
import { AxiosError, AxiosResponse } from 'axios';
import React, { RefObject, useEffect, useRef, useState } from 'react';
import {
    IProblemEvent,
    EProblemFeedbackType,
    IProblemQuestion,
    IProblemResponse,
    EProblemTypeEvent,
    EProblemFeedback,
    EProblemFeedbackPhase,
} from '../../models/Problems.interfaces';
import { getEventsProblemById, getProblemDialogById, nextQuestion } from '../../services/ProblemService';
import './index.scss';
import { useDispatch, useSelector } from 'react-redux';
import { resetResponses, updateResponses } from '../../utils/store/features/chat';
import { getUser } from '../../utils/store/store';
import navigateParams from '../../utils/navigation/navigateParams';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { handleOrderByTimeEvents, literalsFeedback } from '../../pages/support/utils.chat';
import { LoaderMessage } from '../../pages/support/utils.chat';
import { useTranslation } from 'react-i18next';
import ChatDiagnosisFinished from '../ChatDiagnosisFinished';
import ChatDynamicQuestion from '../ChatDynamicQuestion';
import ChatSupportTest from '../ChatSupportChecks';

export const ChatDiagnosisDynamic: React.FC<{ problemId: string }> = ({ problemId }: { problemId: string }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const [params] = useSearchParams();
    const { t } = useTranslation();
    const { dialog } = useDialog();
    const state = useSelector(getUser);
    const TIME_MILISECONDS = 1000;
    const lastEventRef = useRef<string | null>(null);
    const REQUEST_EVENTS_CHAT = 500;
    const TIME_WATING_INIT_QUESTION = 5000;
    const LIMIT_EVENTS_CHAT = 50;
    const secondsPulling = useRef<number>(REQUEST_EVENTS_CHAT);
    const countRequestPulling = useRef<number>(0);
    const intervalPullingRef = useRef<number>();
    const initialIntervalPullingRef = useRef<number>();
    const [question, setQuestion] = useState<IProblemQuestion>();
    const [loading, setLoading] = useState<boolean>(true);
    const [loadingTest, setLoadingTest] = useState<boolean>(false);
    const [finish, setFinish] = useState<boolean>(false);
    const intervalTimout = useRef<number | null>(null);
    const contactWhatsapp = process.env.REACT_APP_WEBVIEW_NUMBER_CONTACT_WHATSAPP as string;

    useEffect(() => {
        initQuestion();
    }, [dispatch]);

    const handleResponseAutomatic = ({ data }: { data: IProblemQuestion }) => {
        sleep(data.timeout! * TIME_MILISECONDS).then(() => {
            window.clearTimeout(intervalTimout.current!);
            setLoading(true);
            nextQuestion({ dialogId: data?.id, problemId, responseId: data.responses[0].id })
                .then(() => {
                    const _response = {
                        dialogId: data?.id!,
                        next: true,
                        question: data,
                        response: data.responses[0]!,
                    };
                    window.clearTimeout(intervalTimout.current!);
                    handleNextQuestion(_response);
                    setLoading(false);
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    window.clearInterval(intervalPullingRef.current);
                    setLoading(false);
                    const queryParams = {
                        phase: EProblemFeedbackType.ERROR,
                        error: EProblemFeedback.DIAGNOSIS,
                        ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                    };
                    if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                        navigate(`/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`);
                    }
                });
        });
    };

    const handleGetDialog = ({ dialogId, problemId }: { dialogId: string; problemId: string }) => {
        setLoading(true);
        window.clearTimeout(initialIntervalPullingRef.current);
        lastEventRef.current = dialogId;
        getProblemDialogById({ dialogId, problemId })
            .then((response: AxiosResponse) => {
                const { data }: { data: IProblemQuestion } = response;
                setLoading(false);
                setLoadingTest(false);
                window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
                setQuestion({ ...data, next: false });

                if (typeof data?.timeout === 'number' && data.responses.length === 1) {
                    handleResponseAutomatic({ data });
                }
            })
            .catch((error: AxiosError) => {
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                window.clearInterval(intervalPullingRef.current);
                const queryParams = {
                    phase: EProblemFeedbackType.ERROR,
                    error: EProblemFeedback.DIAGNOSIS,
                    ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                };
                if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                    navigate(`/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`);
                }
                console.error(error);
            });
    };

    const handleAlertEventWarning = (type: 'inactivity' | 'transfer_channel') => {
        const params = {
            inactivity: {
                description: t('supportWarningInactivity'),
            },
            transfer_channel: {
                description: t('supportWarningCall'),
            },
        };

        dialog({
            icon: <IconWarningRegular color={skinVars.colors.feedbackErrorBackground} size={53} />,
            message: (() => {
                return (
                    <Stack space={16}>
                        <Text3 light color={skinVars.colors.textSecondary}>
                            {params[type].description}
                        </Text3>
                    </Stack>
                );
            })() as any,
            acceptText: t('goThroughWhatsapp'),
            cancelText: t('back'),
            onAccept: () =>
                window.open(
                    `https://wa.me/${contactWhatsapp}?text=Quiero%20continuar%20con%20las%20comprobaciones%20para%20resolver%20mi%20incidencia`,
                    '_blank',
                ),
            onCancel: () => navigate('/support/incidents'),
        });
    };

    const handleEventsPulling = async (time: number) => {
        window.clearTimeout(initialIntervalPullingRef.current);
        const nextPulling = (type: EProblemTypeEvent) => {
            if (type === EProblemTypeEvent.TEST) {
                setLoading(false);
                setLoadingTest(true);
            } else {
                setLoadingTest(false);
                setLoading(true);
            }
            window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
            countRequestPulling.current += 1;
            secondsPulling.current = secondsPulling.current * 1.5;
            window.clearInterval(intervalPullingRef.current);
            handleEventsPulling(secondsPulling.current);
        };

        const resetPulling = (dialogId: string, problemId: string, type: EProblemTypeEvent) => {
            if (type === EProblemTypeEvent.ANSWER || type === EProblemTypeEvent.DIAGNOSIS_RESUMED) {
                nextPulling(type);
            } else {
                window.clearInterval(intervalPullingRef.current);
                secondsPulling.current = REQUEST_EVENTS_CHAT;
                countRequestPulling.current = 0;
                lastEventRef.current = dialogId;
                handleMessageEvents(type, problemId, dialogId);
            }
        };

        intervalPullingRef.current = window.setInterval(() => {
            new Promise((resolve, reject) => {
                resolve(getEventsProblemById({ problemId }));
            })
                .then((response: unknown) => {
                    const { data }: { data: IProblemEvent[] } = response as AxiosResponse;
                    const { dialogId, problemId, type } = handleOrderByTimeEvents(data).at(-1) as IProblemEvent;
                    const trace = {
                        'preview request dialogId': lastEventRef.current,
                        'current request dialogId': dialogId,
                        'acumulator delay request': secondsPulling.current,
                        'counter request events': countRequestPulling.current,
                        'number limit request events': LIMIT_EVENTS_CHAT,
                        'limit request events': countRequestPulling.current === LIMIT_EVENTS_CHAT,
                        'type request event': type,
                    };
                    console.table(trace);
                    const responses = [
                        EProblemTypeEvent.ERROR,
                        EProblemTypeEvent.DIAGNOSIS_ENDED,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_CANCELED,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_TRANSFER_CHANNEL,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_TESTING_PENDING,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_SHIPMENT_PENDING,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_VALIDATION_PENDING,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_CONTINUE_WITHOUT_INTERACTION,
                        EProblemTypeEvent.DIAGNOSIS_ENDED_TECHNICIAN_ASSIGNMENT_PENDING,
                    ];

                    if (responses.includes(type)) {
                        resetPulling(dialogId, problemId, type);
                    } else {
                        if (countRequestPulling.current === LIMIT_EVENTS_CHAT) {
                            window.clearInterval(intervalPullingRef.current);
                            setLoading(false);
                        } else {
                            if (
                                type === EProblemTypeEvent.TEST ||
                                type === EProblemTypeEvent.ANSWER ||
                                type === EProblemTypeEvent.DIAGNOSIS_RESUMED
                            ) {
                                nextPulling(type);
                            } else if (lastEventRef.current === dialogId && type === EProblemTypeEvent.QUESTION) {
                                nextPulling(type);
                                setLoading(false);
                            } else {
                                resetPulling(dialogId, problemId, type);
                            }
                        }
                    }
                })
                .catch((error: AxiosError) => {
                    window.clearInterval(intervalPullingRef.current);
                    console.error(error);
                    const queryParams = {
                        phase: EProblemFeedbackType.ERROR,
                        error: EProblemFeedback.DIAGNOSIS,
                        ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
                    };
                    if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                        navigate(`/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`);
                    }
                });
        }, time);
    };

    const handleNextQuestion = (dialog?: { dialogId: string; next: boolean; response?: IProblemResponse }) => {
        setLoading(true);
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
        window.clearTimeout(intervalTimout.current!);
        handleEventsPulling(REQUEST_EVENTS_CHAT);
        dispatch(updateResponses(dialog));
    };

    const sleep = (ms: number) => {
        return new Promise((resolve) => {
            intervalTimout.current = window.setTimeout(resolve, ms);
        });
    };

    const handleGetCodeIssueAndNavigationFeedback = (queryParams: { [key: string]: string }) => {
        window.clearInterval(intervalPullingRef.current);
        if (params.get('action') === EProblemTypeEvent.DIAGNOSIS_RESUMED) {
            if (navigateParams('feedback-success', '/support/incidents-feedback', queryParams, {}, '')) {
                navigate(
                    `/support/incidents-feedback?type=${queryParams.type}&phase=${queryParams.phase}&code=${params.get(
                        'code',
                    )}`,
                );
            }
        } else {
            if (navigateParams('feedback-success', '/support/incidents-feedback', queryParams, {}, '')) {
                navigate(
                    `/support/incidents-feedback?type=${queryParams.type}&phase=${queryParams.phase}&code=${params.get(
                        'code',
                    )}`,
                );
            }
        }
    };

    const handleMessageEvents = (type: EProblemTypeEvent, problemId: string, dialogId: string) => {
        window.clearTimeout(initialIntervalPullingRef.current);
        EProblemFeedbackPhase.CUR;
        const queryParams = {
            success: {
                type: EProblemFeedbackType.SUCCESS,
                ...literalsFeedback(EProblemFeedback.SUCCESS),
            },
            revision_tech: {
                type: EProblemFeedbackType.INFO,
                phase: EProblemFeedbackPhase.REV_TECH,
                ...literalsFeedback(EProblemFeedback.REVISION_TECHNICAL),
            },
            pending: {
                type: EProblemFeedbackType.INFO,
                phase: EProblemFeedbackPhase.CUR,
                ...literalsFeedback(EProblemFeedback.PENDING),
            },
            shipment_pending: {
                type: EProblemFeedbackType.INFO,
                phase: EProblemFeedbackPhase.SHIPMENT_PENDING,
                ...literalsFeedback(EProblemFeedback.SHIPMENT_PENDING),
            },
            technician_assignment_pending: {
                type: EProblemFeedbackType.INFO,
                phase: EProblemFeedbackPhase.TECHNICIAN_ASSIGNMENT_PENDING,
                ...literalsFeedback(EProblemFeedback.TECHNICIAN_ASSIGNMENT_PENDING),
            },
            logistic_shipping: {
                type: EProblemFeedbackType.INFO,
                phase: EProblemFeedbackPhase.LOGISTIC_SHIPPING,
                ...literalsFeedback(EProblemFeedback.REVISION_TECHNICAL),
            },
            error: {
                phase: EProblemFeedbackType.ERROR,
                error: EProblemFeedback.DIAGNOSIS,
                ...literalsFeedback(EProblemFeedback.SERVICE),
            },
            error_diagnosis: {
                phase: EProblemFeedbackType.ERROR,
                error: EProblemFeedback.DIAGNOSIS,
                ...literalsFeedback(EProblemFeedback.DIAGNOSIS),
            },
        };

        switch (type) {
            case EProblemTypeEvent.ERROR:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams.error)) {
                    navigate(
                        `/support/incidents-feedback?phase=${queryParams.error.phase}&error=${queryParams.error.error}`,
                    );
                }
                break;
            case EProblemTypeEvent.QUESTION:
                setLoading(false);
                setLoadingTest(false);
                handleGetDialog({ dialogId, problemId });
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED:
            case EProblemTypeEvent.DIAGNOSIS_ENDED_CANCELED:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                handleGetCodeIssueAndNavigationFeedback(queryParams.success);
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED_TESTING_PENDING:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                handleGetCodeIssueAndNavigationFeedback(queryParams.pending);
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED_SHIPMENT_PENDING:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                handleGetCodeIssueAndNavigationFeedback(queryParams.shipment_pending);
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED_TRANSFER_CHANNEL:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                handleAlertEventWarning('transfer_channel');
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED_VALIDATION_PENDING:
                setFinish(true);
                setLoading(false);
                setLoadingTest(false);
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED_CONTINUE_WITHOUT_INTERACTION:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                handleGetCodeIssueAndNavigationFeedback(queryParams.revision_tech);
                break;
            case EProblemTypeEvent.DIAGNOSIS_ENDED_TECHNICIAN_ASSIGNMENT_PENDING:
                setLoading(false);
                setLoadingTest(false);
                dispatch(resetResponses());
                handleGetCodeIssueAndNavigationFeedback(queryParams.technician_assignment_pending);
                break;
            default:
                break;
        }
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
    };

    const initQuestion = () => {
        setLoading(true);
        window.scrollTo({ left: 0, top: document.body.scrollHeight, behavior: 'smooth' });
        initialIntervalPullingRef.current = window.setTimeout(() => {
            getEventsProblemById({ problemId })
                .then((response: AxiosResponse) => {
                    const { data }: { data: IProblemEvent[] } = response;
                    const { dialogId, problemId, type } = handleOrderByTimeEvents(data).at(-1) as IProblemEvent;

                    if (type === EProblemTypeEvent.TEST || data.length >= 0) {
                        handleEventsPulling(REQUEST_EVENTS_CHAT);
                    } else if ([EProblemTypeEvent.QUESTION].includes(type)) {
                        handleGetDialog({ dialogId, problemId });
                    } else {
                        handleMessageEvents(type, problemId, dialogId);
                    }
                })
                .catch((error: AxiosError) => {
                    console.error(error);
                    window.clearInterval(intervalPullingRef.current);
                    const queryParams = {
                        phase: EProblemFeedbackType.ERROR,
                        error: EProblemFeedback.INCIDENCE,
                        ...literalsFeedback(EProblemFeedback.INCIDENCE),
                    };
                    if (navigateParams('feedback-error', '/support/incidents-feedback', queryParams)) {
                        navigate(`/support/incidents-feedback?phase=${queryParams.phase}&error=${queryParams.error}`);
                    }
                });
        }, TIME_WATING_INIT_QUESTION);
    };

    const printConversations = () => {
        return state.chatResponse.responses.map((item: any, key: number) => {
            return (
                <ChatDynamicQuestion
                    key={key}
                    answer={item.next}
                    item={{ ...item.question!, response: item.response }}
                    problemId={problemId}
                    onQuestion={(dialog?: {
                        dialogId: string;
                        next: boolean;
                        response?: IProblemResponse;
                        interval: RefObject<number>;
                    }) => {
                        handleNextQuestion(dialog);
                    }}
                />
            );
        });
    };

    return (
        <div className='chat-diagnosis'>
            {printConversations()}
            {loadingTest ? (
                <ChatSupportTest />
            ) : loading ? (
                LoaderMessage
            ) : (
                <>
                    {finish ? (
                        <ChatDiagnosisFinished problemId={problemId} />
                    ) : (
                        <ChatDynamicQuestion
                            key={Math.random()}
                            item={question!}
                            problemId={problemId}
                            onQuestion={(dialog?: {
                                dialogId: string;
                                next: boolean;
                                response?: IProblemResponse;
                                interval: RefObject<number>;
                                logout: boolean;
                            }) => handleNextQuestion(dialog)}
                        />
                    )}
                </>
            )}
        </div>
    );
};

export default ChatDiagnosisDynamic;
